import { getCharacterImageAltBySlug } from "@/lib/image-alt";
import type { CharacterCardMetaobject, ShopifyImage } from "@/lib/shopify";
import { characterField, characterImage } from "@/lib/shopify";
import { characters } from "@/lib/fragrance-data";
import noteViolet from "@/assets/note-violet.jpg?format=webp&quality=72";
import noteClove from "@/assets/note-clove.jpg?format=webp&quality=72";
import noteStarAnise from "@/assets/note-star-anise.jpg?format=webp&quality=72";
import noteCedarwood from "@/assets/note-cedarwood.jpg?format=webp&quality=72";
import noteVetiver from "@/assets/note-vetiver.jpg?format=webp&quality=72";
import noteRose from "@/assets/note-rose.jpg?format=webp&quality=72";
import noteLabdanum from "@/assets/note-labdanum.jpg?format=webp&quality=72";
import noteAmber from "@/assets/note-amber.jpg?format=webp&quality=72";
import noteMastic from "@/assets/note-mastic.jpg?format=webp&quality=72";
import noteMyrrh from "@/assets/note-myrrh.jpg?format=webp&quality=72";
import noteFrankincense from "@/assets/note-frankincense.jpg?format=webp&quality=72";
import noteLavender from "@/assets/note-lavender.jpg?format=webp&quality=72";
import noteTuberose from "@/assets/note-tuberose.jpg?format=webp&quality=72";
import noteBlackTea from "@/assets/note-black-tea.jpg?format=webp&quality=72";
import noteHay from "@/assets/note-hay.jpg?format=webp&quality=72";
import noteLemon from "@/assets/note-lemon.jpg?format=webp&quality=72";
import noteHoney from "@/assets/note-honey.jpg?format=webp&quality=72";
import noteGuaiacwood from "@/assets/note-guaiacwood.jpg?format=webp&quality=72";
import noteTonkaBean from "@/assets/note-tonka-bean.jpg?format=webp&quality=72";
import notePinkPepper from "@/assets/note-pink-pepper.jpg?format=webp&quality=72";
import noteMarshmallow from "@/assets/note-marshmallow.jpg?format=webp&quality=72";
import notePatchouli from "@/assets/note-patchouli.jpg?format=webp&quality=72";
import noteVanilla from "@/assets/note-vanilla.jpg?format=webp&quality=72";
import noteSweetFennel from "@/assets/note-sweet-fennel.jpg?format=webp&quality=72";
import noteOakmoss from "@/assets/note-oakmoss.jpg?format=webp&quality=72";
import noteCardamon from "@/assets/note-cardamon.jpg?format=webp&quality=72";
import noteOakwood from "@/assets/note-oakwood.jpg?format=webp&quality=72";
import noteRosemary from "@/assets/note-rosemary.jpg?format=webp&quality=72";
import noteHoWood from "@/assets/note-ho-wood.jpg?format=webp&quality=72";
import noteJuniperBerry from "@/assets/note-juniper-berry.jpg?format=webp&quality=72";
import notePalmarosa from "@/assets/note-palmarosa.jpg?format=webp&quality=72";
import noteSandalwood from "@/assets/note-sandalwood.jpg?format=webp&quality=72";

export type NoteCard = {
  handle: string;
  name: string;
  subtitle: string;
  blurb: string;
  paragraphs: string[];
  imageUrl: string;
  imageAlt: string;
  characterSlug?: string;
};

// Map Shopify metaobject handle -> curated local image.
const LOCAL_IMAGE_BY_HANDLE: Record<string, string> = {
  violet: noteViolet,
  clove: noteClove,
  "star-anise": noteStarAnise,
  cedarwood: noteCedarwood,
  vetiver: noteVetiver,
  rose: noteRose,
  labdanum: noteLabdanum,
  amber: noteAmber,
  mastic: noteMastic,
  myrrh: noteMyrrh,
  frankincense: noteFrankincense,
  lavender: noteLavender,
  tuberose: noteTuberose,
  "black-tea": noteBlackTea,
  hay: noteHay,
  lemon: noteLemon,
  honey: noteHoney,
  guaiacwood: noteGuaiacwood,
  "tonka-bean": noteTonkaBean,
  "pink-pepper": notePinkPepper,
  marshmallow: noteMarshmallow,
  patchouli: notePatchouli,
  vanilla: noteVanilla,
  "sweet-fennel": noteSweetFennel,
  oakmoss: noteOakmoss,
  cardamon: noteCardamon,
  oakwood: noteOakwood,
  rosemary: noteRosemary,
  "ho-wood": noteHoWood,
  "juniper-berry": noteJuniperBerry,
  palmarosa: notePalmarosa,
  sandalwood: noteSandalwood,
};

// ---------- Shopify rich-text renderer ----------
type RTNode = {
  type: string;
  value?: string;
  bold?: boolean;
  italic?: boolean;
  children?: RTNode[];
  listType?: string;
};

function inlineText(nodes: RTNode[] | undefined): string {
  if (!nodes) return "";
  return nodes
    .map((n) => {
      if (n.type === "text") return n.value ?? "";
      if (n.children) return inlineText(n.children);
      return "";
    })
    .join("")
    .replace(/\u00a0/g, " ")
    .trim();
}

function isAllItalic(nodes: RTNode[] | undefined): boolean {
  if (!nodes || nodes.length === 0) return false;
  return nodes.every((n) => {
    if (n.type === "text") return Boolean(n.italic) || !n.value?.trim();
    if (n.children) return isAllItalic(n.children);
    return true;
  });
}

function renderRichText(raw: string | null | undefined): {
  paragraphs: string[];
  blurb: string;
} {
  if (!raw) return { paragraphs: [], blurb: "" };
  let root: RTNode;
  try {
    root = JSON.parse(raw);
  } catch {
    // Plain text fallback
    const paras = raw
      .split(/\n\s*\n+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return { paragraphs: paras, blurb: paras[0] ?? "" };
  }

  const paragraphs: string[] = [];
  let blurb = "";

  for (const child of root.children ?? []) {
    if (child.type === "paragraph") {
      const text = inlineText(child.children);
      if (!text) continue;
      if (!blurb && isAllItalic(child.children)) {
        blurb = text;
        continue;
      }
      paragraphs.push(text);
    } else if (child.type === "list") {
      for (const item of child.children ?? []) {
        const text = inlineText(item.children);
        if (text) paragraphs.push(text);
      }
    } else if (child.type === "heading") {
      const text = inlineText(child.children);
      if (text) paragraphs.push(text);
    }
  }

  if (!blurb) {
    // Fallback: first sentence of first paragraph, so the card blurb stays one line.
    const first = paragraphs[0] ?? "";
    const match = first.match(/^.+?[.!?](?:\s|$)/);
    blurb = (match ? match[0] : first).trim();
  }
  return { paragraphs, blurb };
}

// Best-effort mapping from a metaobject handle to a local Character slug,
// used so cards for the original 7 notes still link to /character-archive/{slug}.
function findCharacterSlug(handle: string, name: string | null): string | undefined {
  const direct = characters.find((c) => c.slug === handle || c.slug.startsWith(`${handle}-`));
  if (direct) return direct.slug;
  if (name) {
    const lower = name.toLowerCase();
    const byName = characters.find((c) => c.name.toLowerCase() === lower);
    if (byName) return byName.slug;
  }
  return undefined;
}

export function metaobjectToNoteCard(card: CharacterCardMetaobject): NoteCard {
  const name = characterField(card, "name") ?? card.handle;
  const subtitle = characterField(card, "subtitle") ?? "";
  const liveImage: ShopifyImage | null = characterImage(card, "image");
  const { paragraphs, blurb } = renderRichText(characterField(card, "description"));
  const characterSlug = findCharacterSlug(card.handle, name);

  const imageUrl =
    LOCAL_IMAGE_BY_HANDLE[card.handle] ?? liveImage?.url ?? "";
  const imageAlt =
    liveImage?.altText ||
    getCharacterImageAltBySlug(characterSlug ?? "") ||
    `${name}${subtitle ? `, ${subtitle}` : ""} — a botanical still-life portrait in low candlelight`;

  // Prefer the local character tagline so the card matches the character page.
  const localCharacter = characterSlug
    ? characters.find((c) => c.slug === characterSlug)
    : undefined;
  const finalBlurb = localCharacter?.tagline ?? blurb;

  return {
    handle: card.handle,
    name,
    subtitle,
    blurb: finalBlurb,
    paragraphs,
    imageUrl,
    imageAlt,
    characterSlug,
  };
}

export function deriveLiveNoteCards(
  nodes: CharacterCardMetaobject[] | undefined,
): NoteCard[] {
  if (!nodes || nodes.length === 0) return [];
  return nodes.map(metaobjectToNoteCard);
}

// Fallback NoteCards derived from the local `characters` list — used when
// Shopify is unreachable but a product carries local `noteSlugs`.
export function noteCardsFromCharacterSlugs(slugs: string[]): NoteCard[] {
  return slugs
    .map((slug) => characters.find((c) => c.slug === slug))
    .filter((c): c is NonNullable<typeof c> => Boolean(c))
    .map((c) => ({
      handle: c.slug,
      name: c.name,
      subtitle: c.epithet,
      blurb: c.cardBlurb,
      paragraphs: c.paragraphs,
      imageUrl: c.image,
      imageAlt: `${c.name} — ${c.epithet}`,
      characterSlug: c.slug,
    }));
}
